import { getCLS, getFID, getLCP, Metric, PerformanceEventTiming } from 'web-vitals';
import { pick } from 'lodash';
import { webVitalsEvent } from 'lib/Tracking/WebVitals';
import defaultLogger from 'lib/EventLogger';
import { EventData } from 'lib/EventLogger/Event';

interface LargestContentfulPaint extends PerformanceEntry {
  renderTime: DOMHighResTimeStamp;
  loadTime: DOMHighResTimeStamp;
  size: number;
  id: string;
  url: string;
  element: HTMLElement | null;
  toJSON(): Record<string, unknown>;
}

interface LayoutShift extends PerformanceEntry {
  value: number;
  hadRecentInput: boolean;
  lastInputTime: DOMHighResTimeStamp;
  sources: LayoutShiftAttribution[];
}

interface LayoutShiftAttribution {
  node: HTMLElement | null;
  currentRect: DOMRectReadOnly;
  previousRect: DOMRectReadOnly;
}

function unwrapElem(
  element: HTMLElement | null
): Pick<HTMLElement, 'id' | 'className' | 'tagName'> | null {
  return element && pick(element, ['id', 'className', 'tagName']);
}

function jsonifyCLS(metric: Metric): Metric {
  return Object.assign({}, metric, {
    entries: metric.entries.map((entry) => {
      const layoutShift = entry as LayoutShift;
      return Object.assign({}, layoutShift.toJSON(), {
        sources: layoutShift.sources.map((source) => {
          const sourceNode = unwrapElem(source.node);
          return Object.assign({}, pick(source, ['previousRect', 'currentRect']), {
            node: sourceNode,
          });
        }),
      });
    }),
  });
}

function jsonifyFID(metric: Metric): Metric {
  return Object.assign({}, metric, {
    entries: metric.entries.map((entry) => {
      const fid = entry as PerformanceEventTiming;
      return Object.assign({}, fid.toJSON(), {
        target: unwrapElem(fid.target as HTMLElement),
      });
    }),
  });
}

function jsonifyLCP(metric: Metric): Metric {
  return Object.assign({}, metric, {
    entries: metric.entries.map((entry) => {
      const lcp = entry as LargestContentfulPaint;
      return Object.assign({}, lcp.toJSON(), {
        element: unwrapElem(lcp.element as HTMLElement),
      });
    }),
  });
}

export interface Logger {
  log(eventData: EventData): Promise<void>;
}

export default function logWebVitals(logger: Logger = defaultLogger): void {
  function logMetric(metric: Metric): Promise<void> {
    return logger.log(webVitalsEvent(window.location.href, metric));
  }

  if (process.browser) {
    getCLS((metric) => {
      return logMetric(jsonifyCLS(metric));
    }, false);
    getFID((metric) => {
      return logMetric(jsonifyFID(metric));
    }, false);
    getLCP((metric) => {
      return logMetric(jsonifyLCP(metric));
    }, false);
  }
}
